import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../api';
import useAuthStore, { selectIsLoggedIn } from '../../store/authStore';
import { NAV } from '../../config/siteConfig';
import { Icon, Avatar } from '../ui';
import { IsoGlyph } from '../iso/IsoSvg';
import './layout.css';

const GLYPH_FOR = {
  '/quote': 'upload',
  '/materials': 'sheets',
  '/viewer': 'nest',
  '/faq': 'doc',
  '/contact': 'inbox'
};

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useAuthStore(selectIsLoggedIn);
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);

  const [openMenu, setOpenMenu] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userRef = useRef(null);
  const closeTimer = useRef(null);

  const { data: unread = 0 } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.notifications.unreadCount(),
    enabled: isLoggedIn,
    refetchInterval: 60000,
    staleTime: 20000
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setDrawer(false);
    setUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDown = e => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenu(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        setUserMenu(false);
        setDrawer(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = drawer ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawer]);

  const enter = label => {
    clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const leave = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 140);
  };

  const handleLogout = async () => {
    await logout();
    setUserMenu(false);
    setDrawer(false);
    navigate('/');
  };

  return (
    <>
      <header className={`hdr ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="wrap hdr-in">
          <Link to="/" className="hdr-logo" aria-label="Trinity Laser Profile — home">
            <img src="/static/images/logo.svg" alt="Trinity Laser Profile" />
          </Link>

          <nav className="hdr-nav" aria-label="Primary">
            {NAV.primary.map(item =>
              item.children ? (
                <div
                  key={item.label}
                  className={`hdr-item ${openMenu === item.label ? 'is-open' : ''}`}
                  onMouseEnter={() => enter(item.label)}
                  onMouseLeave={leave}
                >
                  <button
                    type="button"
                    className="hdr-link"
                    aria-expanded={openMenu === item.label}
                    onClick={() => setOpenMenu(openMenu === item.label ? null : item.label)}
                  >
                    {item.label}
                    <Icon name="chevronDown" size={14} />
                  </button>
                  <div className="hdr-drop">
                    {item.children.map(c => (
                      <Link key={c.to} to={c.to} className="hdr-drop-item">
                        <IsoGlyph name={GLYPH_FOR[c.to] || 'plate'} size={44} />
                        <span>
                          <b>{c.label}</b>
                          <small>{c.desc}</small>
                        </span>
                        <Icon name="arrowRight" size={14} className="arrow" />
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div key={item.label} className="hdr-item">
                  <NavLink to={item.to} end={item.end} className={({ isActive }) => `hdr-link ${isActive ? 'is-active' : ''}`}>
                    {item.label}
                  </NavLink>
                </div>
              )
            )}
          </nav>

          <div className="hdr-right">
            <Link to="/search" className="btn-icon hdr-ico" aria-label="Search" title="Search">
              <Icon name="search" />
            </Link>
            <Link to="/quote" className="btn btn-p btn-sm hdr-quote">
              <Icon name="upload" />
              Get a quote
            </Link>

            {isLoggedIn ? (
              <>
                <Link to="/notifications" className="btn-icon hdr-ico hdr-bell" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} title="Notifications">
                  <Icon name="bell" />
                  {unread > 0 && <span className="hdr-badge">{unread > 9 ? '9+' : unread}</span>}
                </Link>
                <div className="hdr-user" ref={userRef}>
                  <button type="button" className="hdr-user-btn" onClick={() => setUserMenu(v => !v)} aria-expanded={userMenu} aria-haspopup="menu">
                    <Avatar user={user} />
                    <span className="hdr-user-name">{user?.name?.split(' ')[0]}</span>
                    <Icon name="chevronDown" size={14} className={userMenu ? 'rot' : ''} />
                  </button>
                  {userMenu && (
                    <div className="hdr-user-menu" role="menu">
                      <div className="hdr-user-head">
                        <Avatar user={user} />
                        <div className="grow">
                          <div className="truncate" style={{ fontWeight: 600 }}>{user?.name}</div>
                          <div className="truncate muted xs">{user?.email}</div>
                        </div>
                      </div>
                      <div className="hr" style={{ margin: '8px 0' }} />
                      <Link to="/mypage" role="menuitem"><Icon name="grid" size={16} /> Dashboard</Link>
                      <Link to="/mypage/orders" role="menuitem"><Icon name="package" size={16} /> Orders</Link>
                      <Link to="/mypage/quotes" role="menuitem"><Icon name="fileText" size={16} /> Quotes</Link>
                      <Link to="/mypage/certificates" role="menuitem"><Icon name="shield" size={16} /> Certificates</Link>
                      <Link to="/mypage/profile" role="menuitem"><Icon name="user" size={16} /> Profile</Link>
                      <div className="hr" style={{ margin: '8px 0' }} />
                      <button type="button" role="menuitem" onClick={handleLogout} className="danger">
                        <Icon name="logout" size={16} /> Sign out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="hdr-auth">
                <Link to="/login" className="btn btn-g btn-sm">Sign in</Link>
                <Link to="/signup" className="btn btn-s btn-sm">Create account</Link>
              </div>
            )}

            <button type="button" className="btn-icon hdr-burger" onClick={() => setDrawer(true)} aria-label="Open menu">
              <Icon name="menu" />
            </button>
          </div>
        </div>
      </header>

      {/* ── 모바일 드로어 ── */}
      <div className={`drawer ${drawer ? 'is-open' : ''}`} aria-hidden={!drawer}>
        <div className="drawer-bg" onClick={() => setDrawer(false)} />
        <div className="drawer-panel" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="drawer-head">
            <img src="/static/images/logo.svg" alt="Trinity" height="26" />
            <button type="button" className="btn-icon" onClick={() => setDrawer(false)} aria-label="Close menu">
              <Icon name="x" />
            </button>
          </div>

          {isLoggedIn && (
            <Link to="/mypage" className="drawer-user">
              <Avatar user={user} />
              <span className="grow">
                <b className="truncate">{user?.name}</b>
                <small className="truncate">{user?.email}</small>
              </span>
              {unread > 0 && <span className="badge badge--spark">{unread} new</span>}
            </Link>
          )}

          <nav className="drawer-nav">
            <NavLink to="/" end>Home</NavLink>
            {NAV.primary.filter(i => !i.children && i.to !== '/').map(i => (
              <NavLink key={i.to} to={i.to}>
                {i.label}
                <Icon name="chevronRight" size={16} />
              </NavLink>
            ))}
            {NAV.primary.filter(i => i.children).map(group => (
              <div key={group.label} className="drawer-group">
                <div className="kicker kicker--plain">{group.label}</div>
                {group.children.map(c => (
                  <NavLink key={c.to} to={c.to}>
                    {c.label}
                    <Icon name="chevronRight" size={16} />
                  </NavLink>
                ))}
              </div>
            ))}
            {isLoggedIn && (
              <div className="drawer-group">
                <div className="kicker kicker--plain">Account</div>
                <NavLink to="/mypage" end>Dashboard<Icon name="chevronRight" size={16} /></NavLink>
                <NavLink to="/mypage/orders">Orders<Icon name="chevronRight" size={16} /></NavLink>
                <NavLink to="/mypage/quotes">Quotes<Icon name="chevronRight" size={16} /></NavLink>
                <NavLink to="/mypage/certificates">Certificates<Icon name="chevronRight" size={16} /></NavLink>
                <NavLink to="/notifications">Notifications<Icon name="chevronRight" size={16} /></NavLink>
                <NavLink to="/mypage/profile">Profile<Icon name="chevronRight" size={16} /></NavLink>
              </div>
            )}
          </nav>

          <div className="drawer-foot">
            <Link to="/quote" className="btn btn-p btn-block"><Icon name="upload" />Get a quote</Link>
            {isLoggedIn ? (
              <button type="button" className="btn btn-s btn-block" onClick={handleLogout}><Icon name="logout" />Sign out</button>
            ) : (
              <div className="row" style={{ gap: 10 }}>
                <Link to="/login" className="btn btn-s grow">Sign in</Link>
                <Link to="/signup" className="btn btn-soft grow">Create account</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
